// src/admin/components/ImageLightbox.tsx
// Shared full-screen image viewer with prev/next navigation and keyboard
// controls. Originally lived only inside CarDrawer.tsx — extracted so other
// admin pages (e.g. Trade-In Submissions) can show the same viewing
// experience for their own image sets.
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';

interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ images, initialIndex, open, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => { setCurrentIndex(initialIndex); }, [initialIndex, open]);

  const handlePrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleNext = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowLeft') setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setCurrentIndex(prev => (prev + 1) % images.length);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, images.length, onClose]);

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)', boxShadow: 'none', borderRadius: 2, overflow: 'hidden', maxWidth: '90vw', maxHeight: '90vh' } }}
      BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.85)' } }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 300, minHeight: 300 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute', top: 8, right: 8, zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.2)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" sx={{ color: '#ffffff' }} />
        </IconButton>
        <Box component="img" src={images[currentIndex]} alt={`Image ${currentIndex + 1}`} sx={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
        {images.length > 1 && (
          <>
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              <NavigateBeforeIcon sx={{ color: '#ffffff', fontSize: 28 }} />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              }}
            >
              <NavigateNextIcon sx={{ color: '#ffffff', fontSize: 28 }} />
            </IconButton>
          </>
        )}
        {images.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 2, py: 0.5, borderRadius: 10, fontSize: '0.75rem' }}>
            {currentIndex + 1} / {images.length}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageLightbox;
